import React, { useMemo, useState } from 'react';
import { Search, Filter, X, ArrowUpDown, ArrowUp, ArrowDown, User, Building, Briefcase, Calendar } from 'lucide-react';
import { GuardRosterModal } from './GuardRosterModal';
import { EmptyState } from './EmptyState';
interface Guard {
  id: string;
  name: string;
  grade: 'A' | 'B' | 'C';
  site: string;
  province: string;
  currentShifts: number;
  status: 'Over Threshold' | 'Optimal' | 'Under-utilised';
}
const mockGuards: Guard[] = [{
  id: '1',
  name: 'Sipho Dlamini',
  grade: 'A',
  site: 'Woolworths HQ',
  province: 'Western Cape',
  currentShifts: 24,
  status: 'Over Threshold'
}, {
  id: '2',
  name: 'Thabo Molefe',
  grade: 'B',
  site: 'Standard Bank Tower',
  province: 'Gauteng',
  currentShifts: 22,
  status: 'Over Threshold'
}, {
  id: '3',
  name: 'Nomsa Khumalo',
  grade: 'C',
  site: 'Pick n Pay Constantia',
  province: 'Western Cape',
  currentShifts: 21,
  status: 'Over Threshold'
}, {
  id: '4',
  name: 'Lerato Nkosi',
  grade: 'A',
  site: 'Sandton City Mall',
  province: 'Gauteng',
  currentShifts: 25,
  status: 'Over Threshold'
}, {
  id: '5',
  name: 'Bongani Zulu',
  grade: 'B',
  site: 'Durban ICC',
  province: 'KwaZulu-Natal',
  currentShifts: 20,
  status: 'Over Threshold'
}, {
  id: '6',
  name: 'Precious Mokoena',
  grade: 'A',
  site: 'OR Tambo Airport',
  province: 'Gauteng',
  currentShifts: 23,
  status: 'Over Threshold'
}, {
  id: '7',
  name: 'Mandla Sithole',
  grade: 'C',
  site: 'Gateway Theatre',
  province: 'KwaZulu-Natal',
  currentShifts: 21,
  status: 'Over Threshold'
}, {
  id: '8',
  name: 'Zanele Mthembu',
  grade: 'B',
  site: 'V&A Waterfront',
  province: 'Western Cape',
  currentShifts: 24,
  status: 'Over Threshold'
}, {
  id: '9',
  name: 'John Mkhize',
  grade: 'B',
  site: 'Reliever Pool',
  province: 'Western Cape',
  currentShifts: 14,
  status: 'Under-utilised'
}, {
  id: '10',
  name: 'Lindiwe Zulu',
  grade: 'C',
  site: 'Shoprite Bellville',
  province: 'Western Cape',
  currentShifts: 12,
  status: 'Under-utilised'
}, {
  id: '11',
  name: 'David Botha',
  grade: 'A',
  site: 'FNB Foreshore',
  province: 'Western Cape',
  currentShifts: 16,
  status: 'Under-utilised'
}, {
  id: '12',
  name: 'Grace Ndlovu',
  grade: 'B',
  site: 'Checkers Claremont',
  province: 'Western Cape',
  currentShifts: 10,
  status: 'Under-utilised'
}, {
  id: '13',
  name: 'Peter van Wyk',
  grade: 'A',
  site: 'Woolworths Canal Walk',
  province: 'Western Cape',
  currentShifts: 15,
  status: 'Under-utilised'
}, {
  id: '14',
  name: 'Themba Nkosi',
  grade: 'C',
  site: 'Pick n Pay Tokai',
  province: 'Western Cape',
  currentShifts: 11,
  status: 'Under-utilised'
}, {
  id: '15',
  name: 'Sarah Jacobs',
  grade: 'B',
  site: 'Spar Sea Point',
  province: 'Western Cape',
  currentShifts: 13,
  status: 'Under-utilised'
}, {
  id: '16',
  name: 'Michael Dube',
  grade: 'A',
  site: 'Game Parow',
  province: 'Western Cape',
  currentShifts: 19,
  status: 'Optimal'
}, {
  id: '17',
  name: 'James Smith',
  grade: 'B',
  site: 'Nedbank Plaza',
  province: 'Gauteng',
  currentShifts: 19,
  status: 'Optimal'
}, {
  id: '18',
  name: 'Emily Davis',
  grade: 'C',
  site: 'Dischem Warehouse',
  province: 'Gauteng',
  currentShifts: 19,
  status: 'Optimal'
}];
type SortField = 'name' | 'grade' | 'site' | 'province' | 'currentShifts';
type SortDirection = 'asc' | 'desc';
export function AllGuardsView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('All Provinces');
  const [gradeFilter, setGradeFilter] = useState('All Grades');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const clearFilters = () => {
    setSearchQuery('');
    setProvinceFilter('All Provinces');
    setGradeFilter('All Grades');
    setStatusFilter('All Statuses');
  };
  const filteredAndSortedGuards = useMemo(() => {
    let result = mockGuards;
    // Filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(g => g.name.toLowerCase().includes(q) || g.site.toLowerCase().includes(q));
    }
    if (provinceFilter !== 'All Provinces') {
      result = result.filter(g => g.province === provinceFilter);
    }
    if (gradeFilter !== 'All Grades') {
      const grade = gradeFilter.replace('Grade ', '');
      result = result.filter(g => g.grade === grade);
    }
    if (statusFilter !== 'All Statuses') {
      result = result.filter(g => g.status === statusFilter);
    }
    // Sort
    result = [...result].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [searchQuery, provinceFilter, gradeFilter, statusFilter, sortField, sortDirection]);
  const isFiltered = searchQuery || provinceFilter !== 'All Provinces' || gradeFilter !== 'All Grades' || statusFilter !== 'All Statuses';
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Optimal':
        return 'bg-emerald-100 text-emerald-700';
      case 'Under-utilised':
        return 'bg-sky-100 text-sky-700';
      case 'Over Threshold':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-[#e5e5e5]" data-id="element-262" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-[#062133]" data-id="element-263" /> : <ArrowDown className="w-3 h-3 ml-1 text-[#062133]" data-id="element-264" />;
  };
  return <div className="flex flex-col space-y-6 animate-in fade-in duration-300" data-id="element-265">
      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-[#e5e5e5] shadow-sm flex flex-col md:flex-row gap-4" data-id="element-266">
        <div className="relative flex-1" data-id="element-267">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5a7280]" data-id="element-268" />
          <input type="text" placeholder="Search guards or sites..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent transition-colors" data-id="element-269" />
        </div>

        <div className="flex flex-col md:flex-row gap-4 overflow-x-auto pb-2 md:pb-0" data-id="element-270">
          <select value={provinceFilter} onChange={e => setProvinceFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm text-[#062133] focus:outline-none focus:ring-2 focus:ring-[#ff697f] min-w-[140px]" data-id="element-271">
            <option data-id="element-272">All Provinces</option>
            <option data-id="element-273">Western Cape</option>
            <option data-id="element-274">Gauteng</option>
            <option data-id="element-275">KwaZulu-Natal</option>
          </select>

          <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm text-[#062133] focus:outline-none focus:ring-2 focus:ring-[#ff697f] min-w-[120px]" data-id="element-276">
            <option data-id="element-277">All Grades</option>
            <option data-id="element-278">Grade A</option>
            <option data-id="element-279">Grade B</option>
            <option data-id="element-280">Grade C</option>
          </select>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm text-[#062133] focus:outline-none focus:ring-2 focus:ring-[#ff697f] min-w-[160px]" data-id="element-281">
            <option data-id="element-282">All Statuses</option>
            <option data-id="element-283">Optimal</option>
            <option data-id="element-284">Under-utilised</option>
            <option data-id="element-285">Over Threshold</option>
          </select>

          {isFiltered && <button onClick={clearFilters} className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-xl transition-colors" data-id="element-286">
              <X className="h-4 w-4" data-id="element-287" />
              Clear
            </button>}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl border border-[#e5e5e5] shadow-sm overflow-hidden" data-id="element-288">
        <table className="w-full text-left border-collapse" data-id="element-289">
          <thead data-id="element-290">
            <tr className="bg-[#fafaf9] border-b border-[#e5e5e5]" data-id="element-291">
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('name')} data-id="element-292">
                <div className="flex items-center" data-id="element-293">
                  Guard <SortIcon field="name" data-id="element-294" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('grade')} data-id="element-295">
                <div className="flex items-center" data-id="element-296">
                  Grade <SortIcon field="grade" data-id="element-297" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('site')} data-id="element-298">
                <div className="flex items-center" data-id="element-299">
                  Primary Site <SortIcon field="site" data-id="element-300" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('province')} data-id="element-301">
                <div className="flex items-center" data-id="element-302">
                  Province <SortIcon field="province" data-id="element-303" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('currentShifts')} data-id="element-304">
                <div className="flex items-center" data-id="element-305">
                  Shifts <SortIcon field="currentShifts" data-id="element-306" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase" data-id="element-307">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5]" data-id="element-308">
            {filteredAndSortedGuards.length > 0 ? filteredAndSortedGuards.map(guard => <tr key={guard.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => setSelectedGuard(guard)} data-id="element-309">
                  <td className="py-4 px-6" data-id="element-310">
                    <div className="flex items-center gap-3" data-id="element-311">
                      <div className="h-8 w-8 rounded-full bg-[#062133]/5 flex items-center justify-center flex-shrink-0" data-id="element-312">
                        <User className="h-4 w-4 text-[#062133]" data-id="element-313" />
                      </div>
                      <span className="text-sm font-semibold text-[#062133]" data-id="element-314">
                        {guard.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-315">
                    <div className="flex items-center text-sm text-[#5a7280]" data-id="element-316">
                      <Briefcase className="w-4 h-4 mr-1.5" data-id="element-317" /> Grade{' '}
                      {guard.grade}
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-318">
                    <div className="flex items-center text-sm text-[#062133]" data-id="element-319">
                      <Building className="w-4 h-4 mr-1.5 text-[#5a7280]" data-id="element-320" />{' '}
                      {guard.site}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-[#5a7280]" data-id="element-321">
                    {guard.province}
                  </td>
                  <td className="py-4 px-6" data-id="element-322">
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-323">
                      <Calendar className="w-4 h-4 mr-1.5 text-[#5a7280]" data-id="element-324" />{' '}
                      {guard.currentShifts}
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-325">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(guard.status)}`} data-id="element-326">
                      {guard.status}
                    </span>
                  </td>
                </tr>) : <tr data-id="element-327">
                <td colSpan={6} className="p-0" data-id="element-328">
                  <EmptyState icon={User} title="No guards found" description="Try adjusting your search or filters" data-id="element-329" />
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4" data-id="element-330">
        {filteredAndSortedGuards.length > 0 ? filteredAndSortedGuards.map(guard => <div key={guard.id} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 cursor-pointer hover:border-[#5a7280]/30 transition-colors" onClick={() => setSelectedGuard(guard)} data-id="element-331">
              <div className="flex justify-between items-start" data-id="element-332">
                <div className="flex items-center gap-3" data-id="element-333">
                  <div className="h-10 w-10 rounded-full bg-[#062133]/5 flex items-center justify-center flex-shrink-0" data-id="element-334">
                    <User className="h-5 w-5 text-[#062133]" data-id="element-335" />
                  </div>
                  <div data-id="element-336">
                    <h3 className="text-base font-semibold text-[#062133]" data-id="element-337">
                      {guard.name}
                    </h3>
                    <p className="text-xs text-[#5a7280] flex items-center gap-1 mt-0.5" data-id="element-338">
                      <Briefcase className="w-3 h-3" data-id="element-339" />
                      Grade {guard.grade}
                    </p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(guard.status)}`} data-id="element-340">
                  {guard.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 py-3 border-t border-b border-[#e5e5e5]" data-id="element-341">
                <div data-id="element-342">
                  <p className="text-[10px] text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-343">
                    Primary Site
                  </p>
                  <p className="text-sm font-medium text-[#062133] truncate" data-id="element-344">
                    {guard.site}
                  </p>
                </div>
                <div data-id="element-345">
                  <p className="text-[10px] text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-346">
                    Shifts
                  </p>
                  <p className="text-sm font-medium text-[#062133]" data-id="element-347">
                    {guard.currentShifts}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center" data-id="element-348">
                <span className="text-xs text-[#5a7280]" data-id="element-349">{guard.province}</span>
                <span className="text-xs font-medium text-[#ff697f]" data-id="element-350">
                  View Roster →
                </span>
              </div>
            </div>) : <EmptyState icon={User} title="No guards found" description="Try adjusting your search or filters" data-id="element-351" />}
      </div>

      {/* Guard Roster Modal */}
      <GuardRosterModal isOpen={selectedGuard !== null} guardName={selectedGuard?.name || ''} guardId={selectedGuard?.id || ''} grade={`Grade ${selectedGuard?.grade}` || ''} primarySite={selectedGuard?.site || ''} totalShifts={selectedGuard?.currentShifts || 0} variant={selectedGuard?.status === 'Over Threshold' ? 'over-threshold' : 'under-utilised'} onClose={() => setSelectedGuard(null)} data-id="element-352" />
    </div>;
}